/**
 * Link Preview Component
 * Displays rich preview cards for links in messages
 * Supports dismissing previews (X button for message author only)
 */

import { useState } from 'react';
import { useUserSettings } from '@/hooks/useUserSettings';
import { getDisplayHostname, getDisplayUrl } from '@/utils/url.util';

export interface LinkPreviewData {
  id: string;
  url: string;
  title?: string | null;
  description?: string | null;
  image_url?: string | null;
  favicon_url?: string | null;
  site_name?: string | null;
}

interface LinkPreviewProps {
  link: LinkPreviewData;
  compact?: boolean;
  /** Whether the current user is the message author */
  isAuthor?: boolean;
  /** Callback when hide button is clicked */
  onHide?: (linkId: string) => void;
}

export const LinkPreview = ({
  link,
  compact = false,
  isAuthor = false,
  onHide,
}: LinkPreviewProps) => {
  const { data: settings } = useUserSettings();
  const [imageError, setImageError] = useState(false);
  const [isHiding, setIsHiding] = useState(false);

  // Check user settings
  const showPreview = settings?.show_link_previews ?? true;
  const showImages = settings?.show_link_preview_images ?? true;

  const handleHide = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onHide && !isHiding) {
      setIsHiding(true);
      onHide(link.id);
    }
  };

  // Hide button component (only visible to message author on hover)
  const renderHideButton = () => {
    if (!isAuthor || !onHide) return null;

    return (
      <button
        onClick={handleHide}
        disabled={isHiding}
        className="absolute top-2 right-2 p-1 rounded-full bg-gray-800/70 hover:bg-gray-800/90 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-10"
        title="Скрий визуализацията"
        aria-label="Скрий визуализацията на връзката"
      >
        {isHiding ? (
          <svg
            className="w-4 h-4 animate-spin"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        )}
      </button>
    );
  };

  // Don't show if user disabled previews
  if (!showPreview) {
    return (
      <a
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 dark:text-blue-400 hover:underline break-all"
      >
        {link.url}
      </a>
    );
  }

  // Show compact version (just URL with favicon)
  if (compact || (!link.title && !link.description && !link.image_url)) {
    return (
      <div className="relative inline-flex items-center gap-2 group">
        <a
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline max-w-full"
        >
          {link.favicon_url && (
            <img
              src={link.favicon_url}
              alt=""
              className="w-4 h-4 flex-shrink-0"
              onError={() => setImageError(true)}
            />
          )}
          <span className="truncate">
            {link.site_name || getDisplayHostname(link.url)}
          </span>
        </a>
        {isAuthor && onHide && (
          <button
            onClick={handleHide}
            disabled={isHiding}
            className="p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Скрий визуализацията"
            aria-label="Скрий визуализацията на връзката"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
    );
  }

  // Full preview card
  return (
    <div className="relative mt-2 max-w-md group">
      {renderHideButton()}
      <a
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block border border-gray-200 dark:border-dark-border-primary rounded-lg overflow-hidden"
      >
        {/* Preview Image */}
        {showImages && link.image_url && !imageError && (
          <div className="w-full h-32 bg-gray-100 dark:bg-dark-bg-tertiary overflow-hidden">
            <img
              src={link.image_url}
              alt={link.title || ''}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          </div>
        )}

        {/* Content */}
        <div className="p-3 bg-white dark:bg-dark-bg-secondary">
          {/* Site Name / Favicon */}
          {(link.site_name || link.favicon_url) && (
            <div className="flex items-center gap-2 mb-2">
              {link.favicon_url && (
                <img
                  src={link.favicon_url}
                  alt=""
                  className="w-4 h-4"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}
              <span className="text-xs text-gray-500 dark:text-dark-text-muted uppercase tracking-wide">
                {link.site_name || getDisplayHostname(link.url)}
              </span>
            </div>
          )}

          {/* Title */}
          {link.title && (
            <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary line-clamp-2 mb-1">
              {link.title}
            </h3>
          )}

          {/* Description */}
          {link.description && (
            <p className="text-sm text-gray-600 dark:text-dark-text-secondary line-clamp-2">
              {link.description}
            </p>
          )}

          {/* URL */}
          <p className="text-xs text-gray-400 dark:text-dark-text-muted mt-2 truncate">
            {getDisplayUrl(link.url)}
          </p>
        </div>
      </a>
    </div>
  );
};

export default LinkPreview;
